/**
 * @file src/graphql/fetcher.ts
 * @exports fetchDataViaApiKey, isServiceUnavailableError, registerServiceUnavailableHandler, useFetchData
 * @summary GraphQL API client with preserved error details, authentication headers, query-only retries, timeouts, and abort-aware outage detection.
 * @remarks Transport failures are rethrown with the original Axios error as `cause`.
 */

import axios from "axios";
import { type GraphQLFormattedError, getOperationAST, parse } from "graphql";
import { useAtomValue } from "jotai";
import { DeviceTypeEnum } from "src/generated/graphql";
import { cortexApiKeyHeaders } from "src/helpers/cortexApiKeyHeaders";
import { currentOrgIdAtom, vIDStateName } from "src/shame/shame-atoms";
import { isMobileBrowser } from "src/views/VID/views/helpers/isMobileBrowser";
import { useSessionValue } from "src/views/VID/views/hooks/sessionHooks";
import { graphqlEndpointUrl } from "./../environment";
import { GraphQLRequestError } from "./GraphQLRequestError";

interface GraphQLResponse<TData> {
	data: TData;
	errors?: GraphQLFormattedError[];
	message?: string;
}

type ServiceUnavailableHandler = (isUnavailable: boolean) => void;

let serviceUnavailableHandler: ServiceUnavailableHandler | undefined;

/** Registers the manager-owned outage handler (avoids a circular import with `src/manager`). */
export const registerServiceUnavailableHandler = (handler: ServiceUnavailableHandler): void => {
	serviceUnavailableHandler = handler;
};

const setServiceUnavailable = (isUnavailable: boolean): void => {
	serviceUnavailableHandler?.(isUnavailable);
};
// This is a custom fetcher function, used by graphql-codegen to fetch data from the API
// It sets the active organization id in the request header so that the API can use it for processing the request
// See https://the-guild.dev/graphql/codegen/plugins/typescript/typescript-react-query#using-custom-fetcher
export const useFetchData = <TData, TVariables>(
	query: string,
	options?: RequestInit["headers"],
): ((variables?: TVariables) => Promise<TData>) => {
	const orgId = useAtomValue(currentOrgIdAtom);

	// get API key
	const userSession = useSessionValue(vIDStateName.KeyDetails);
	const { apiKey } = userSession;

	return async (variables?: TVariables): Promise<TData> => {
		return fetchDataViaApiKey({ query, orgId, apiKey, variables, options });
	};
};

interface FetchDataViaApiKey<TVariables = any> {
	query: string;
	orgId: string | null;
	apiKey: string;
	variables?: TVariables;
	options?: RequestInit["headers"];
	background?: boolean;
}

const GRAPHQL_RETRYABLE_STATUSES = new Set([502, 503, 504]);
const GRAPHQL_MAX_ATTEMPTS = 4;
const GRAPHQL_INITIAL_BACKOFF_MS = 250;
const GRAPHQL_TIMEOUT_MS = 15_000;

const getOperationName = (query: string): string | undefined => {
	const operationMatch = query.match(/\b(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)/);

	return operationMatch?.[1];
};

const getGraphqlEndpointUrl = (operationName?: string): string => {
	if (!operationName) {
		return graphqlEndpointUrl;
	}

	const url = new URL(graphqlEndpointUrl);
	url.searchParams.set("operationName", operationName);

	return url.toString();
};

const shouldSkipApiKeyForQuery = (query: string): boolean => {
	return (
		query.includes("sendApiKeyUnlockVerificationEmail") ||
		query.includes("verifyApiKeyUnlock") ||
		query.includes("getVoucherStatus")
	);
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Browser cancellation signatures can appear independently in name, code, or message.
const isRequestCancellation = (error: unknown): boolean => {
	if (!error || typeof error !== "object") return false;
	const { name, code, message } = error as { name?: string; code?: string; message?: string };
	return [name, code, message].some(
		(value) =>
			typeof value === "string" && /\b(?:AbortError|NS_BINDING_ABORTED|ERR_ABORTED|ERR_CANCELED)\b/.test(value),
	);
};

export const isServiceUnavailableError = (error: unknown): boolean => {
	if (!axios.isAxiosError(error)) {
		return false;
	}

	// Cancellations during navigation/unmount are not service outages.
	if (isRequestCancellation(error)) {
		return false;
	}

	const status = error.response?.status;
	if (status && GRAPHQL_RETRYABLE_STATUSES.has(status)) {
		return true;
	}

	// No HTTP response typically means the backend (or path to it) is unreachable
	// (e.g. network failure or request timeout).
	return !error.response;
};

const getBackoffMs = (attempt: number): number => {
	const exponentialBackoffMs = GRAPHQL_INITIAL_BACKOFF_MS * 2 ** (attempt - 1);
	const jitterFactor = 1 + (Math.random() * 0.4 - 0.2); // +/-20%

	return Math.round(exponentialBackoffMs * jitterFactor);
};

// fetchDataViaApiKey useful if you need to pass an API key directly
export const fetchDataViaApiKey = async <TData, TVariables = any>({
	query,
	orgId,
	apiKey,
	variables,
	options,
	background = false,
}: FetchDataViaApiKey<TVariables>): Promise<TData> => {
	const headers: Record<string, string> = {
		"x-device-type": isMobileBrowser() ? DeviceTypeEnum.WebMobile : DeviceTypeEnum.Web,
		"content-type": "application/json",
		...(options as Record<string, string>),
	};

	if (orgId) {
		headers["x-cerebrum-org-id"] = orgId;
	}

	// Conditionally add the API key to the headers.
	// Some operations (API key unlock flows and getVoucherStatus) must run without apiKey headers.
	if (apiKey && !shouldSkipApiKeyForQuery(query)) {
		Object.assign(headers, cortexApiKeyHeaders(apiKey));
	}

	// Only a single, unambiguous query can safely be replayed.
	let maxAttempts = 1;
	try {
		if (getOperationAST(parse(query))?.operation === "query") maxAttempts = GRAPHQL_MAX_ATTEMPTS;
	} catch {
		// Let the API report invalid documents without replaying them.
	}
	const requestUrl = getGraphqlEndpointUrl(getOperationName(query));
	let lastError: unknown;
	let serviceUnavailableDetected = false;

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const { data: json, status } = await axios.post<GraphQLResponse<TData>>(
				requestUrl,
				{
					query,
					variables,
				},
				{
					withCredentials: true,
					headers,
					timeout: GRAPHQL_TIMEOUT_MS,
				},
			);

			if (json?.errors) {
				const messages = json.errors.map((error) => error.message);
				throw new GraphQLRequestError({ message: messages.join("\n"), errors: json.errors });
			}

			if (status !== 200) {
				throw new Error(json?.message || `HTTP error ${status}`);
			}

			if (!background) setServiceUnavailable(false);
			return json.data;
		} catch (error) {
			let resolvedError: unknown = error;
			serviceUnavailableDetected = isServiceUnavailableError(error);

			if (axios.isAxiosError<GraphQLResponse<TData>>(error)) {
				const graphQLErrors = error.response?.data?.errors;
				const flattenedError = graphQLErrors?.map((error) => error.message).join("\n");

				// Log 400 errors with operation details for debugging
				//   This is specifically for debugging this Sentry issue:
				//   https://cerebrum.sentry.io/issues/6774122098/?environment=production&project=4504136659828736&query=400&referrer=issue-stream
				if (error.response?.status === 400) {
					const operationName = getOperationName(query) ?? "Unknown";

					console.error("❌ GraphQL 400 Error:", {
						operation: operationName,
						error: error.response?.data?.message || flattenedError || error.message,
						variables,
						status: error.response?.status,
					});
				}

				const message = `Request failed: ${error.response?.data?.message || ""} ${flattenedError || ""} ${error.message || ""}`;
				resolvedError = graphQLErrors
					? new GraphQLRequestError({ cause: error, errors: graphQLErrors, message })
					: new Error(message, { cause: error });
			}

			if (isServiceUnavailableError(error) && attempt < maxAttempts) {
				lastError = resolvedError;
				await sleep(getBackoffMs(attempt));
				continue;
			}

			lastError = resolvedError;
			break;
		}
	}

	if (lastError) {
		// A single mutation failure cannot establish an outage; only exhausted queries can.
		if (serviceUnavailableDetected && maxAttempts > 1 && !background) {
			setServiceUnavailable(true);
		}
		throw lastError;
	}

	throw new Error("Request failed after max retry attempts");
};
